import React, { forwardRef } from "react";
import { InputText, InputTextProps } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { DashlyFieldShell } from "../shared/DashlyFieldShell";
import { DashlyControlSize, dashlyControlSizeClass } from "../shared/dashlyControlSize";
import { DashlyControlVariant, dashlyControlVariantClass } from "../shared/dashlyControlVariant";
import {
    DashlyLeadingIcon,
    DashlyLeadingIconSlot,
    resolveLeadingIconClass,
} from "../shared/resolveLeadingIcon";
import { useDashlyFieldValidation } from "../shared/useDashlyFieldValidation";
import { resolveDashlyInputDebounceMs } from "../shared/dashlyInputDebounce";
import { useDebouncedInputChange } from "../shared/useDebouncedInputChange";
import type { FieldValidationRules } from "../../../Util/fieldValidation";
import "../shared/dashlyFormControl.scss";
import "./DashlyTextInput.scss";

export type DashlyTextInputProps = InputTextProps & {
    leadingIcon?: DashlyLeadingIcon;
    trailingIcon?: string;
    /** Debounce `onChange` (ms). Filter variant defaults to 350; form variant defaults to 0. */
    debounceMs?: number;
    /** `filter` — list-page filter bar styling. */
    controlVariant?: DashlyControlVariant;
    /** Control height scale. Default `md` (2.5rem). */
    controlSize?: DashlyControlSize;
    label?: React.ReactNode;
    labelInfo?: string;
    hint?: React.ReactNode;
    required?: boolean;
    containerClassName?: string;
    /** Entity-aligned validation (maxLength, minLength, required, min, max). */
    validation?: FieldValidationRules;
    /** Shows a trailing spinner and disables the control while async work runs. */
    loading?: boolean;
};

const DashlyTextInput = forwardRef<HTMLInputElement, DashlyTextInputProps>(function DashlyTextInput(
    {
        leadingIcon,
        trailingIcon,
        controlVariant = "form",
        controlSize = "md",
        label,
        labelInfo,
        hint,
        required,
        containerClassName,
        validation,
        className,
        id,
        style,
        value,
        maxLength,
        debounceMs,
        loading = false,
        disabled,
        onChange,
        onBlur,
        ...rest
    },
    ref
) {
    const resolvedDebounceMs = resolveDashlyInputDebounceMs(debounceMs, controlVariant);
    const { displayValue, handleChange: debouncedHandleChange, handleBlurFlush } = useDebouncedInputChange({
        value: typeof value === "string" ? value : value == null ? "" : String(value),
        onChange,
        debounceMs: resolvedDebounceMs,
    });
    const { error, invalid, markTouched, resolvedMaxLength } = useDashlyFieldValidation(
        resolvedDebounceMs > 0 ? displayValue : value,
        validation,
    );
    const effectiveMaxLength = maxLength ?? resolvedMaxLength;
    const leadingIconClass = resolveLeadingIconClass(
        leadingIcon,
        resolvedDebounceMs > 0 ? displayValue : value,
    );

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        markTouched();
        debouncedHandleChange(event);
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        markTouched();
        handleBlurFlush(event);
        onBlur?.(event);
    };

    const showTrailing = loading || Boolean(trailingIcon);

    const input = (
        <div
            className={classNames(
                "dashly-text-input-wrap",
                dashlyControlSizeClass(controlSize),
                controlVariant === "filter" && "dashly-text-input-wrap--filter",
                leadingIconClass && "dashly-text-input-wrap--with-icon",
                showTrailing && "dashly-text-input-wrap--with-trailing-icon",
                loading && "dashly-text-input-wrap--loading",
                invalid && "dashly-text-input-wrap--invalid",
            )}
            aria-busy={loading || undefined}
        >
            {leadingIconClass ? (
                <DashlyLeadingIconSlot
                    iconClass={leadingIconClass}
                    className="dashly-text-input-wrap__leading-icon"
                />
            ) : null}
            <InputText
                ref={ref}
                id={id}
                value={displayValue}
                maxLength={effectiveMaxLength}
                aria-invalid={invalid || undefined}
                {...rest}
                disabled={disabled || loading}
                onChange={handleChange}
                onBlur={handleBlur}
                className={classNames(
                    dashlyControlVariantClass("dashly-text-input", controlVariant),
                    "w-full",
                    invalid && "p-invalid",
                    className,
                )}
                style={{
                    ...style,
                    ...(showTrailing ? { paddingRight: "2.25rem" } : undefined),
                }}
            />
            {loading ? (
                <i className="dashly-text-input-wrap__trailing-icon pi pi-spin pi-spinner" aria-hidden />
            ) : trailingIcon ? (
                <i className={classNames("dashly-text-input-wrap__trailing-icon", trailingIcon)} aria-hidden />
            ) : null}
        </div>
    );

    if (label || labelInfo || hint || error) {
        return (
            <DashlyFieldShell
                label={label}
                labelInfo={labelInfo}
                hint={hint}
                error={error}
                htmlFor={id}
                required={required ?? validation?.required}
                className={containerClassName}
            >
                {input}
            </DashlyFieldShell>
        );
    }

    if (error) {
        return (
            <div className={classNames("dashly-field-shell dashly-field-shell--invalid", containerClassName)}>
                {input}
                <span className="dashly-field-shell__error" role="alert">
                    {error}
                </span>
            </div>
        );
    }

    return input;
});

DashlyTextInput.displayName = "DashlyTextInput";

export { DashlyTextInput };
